import isEqual from 'lodash/isEqual';

import {createInputField, disableFormControls} from '../forms/formFields';
import {cloneValue} from '../forms/formState';

const isPlainObject = value => Object.prototype.toString.call(value) === '[object Object]';

const waitMinimumSeconds = 30;

const normalizeString = value => typeof value === 'undefined' || value === null ? '' : String(value);

const normalizeWaitTask = task => {
    if (!isPlainObject(task)) {
        return {
            seconds: '',
            secondsExpression: '',
            timestamp: '',
            timestampExpression: ''
        };
    }

    return {
        ...cloneValue(task),
        seconds: normalizeString(task.seconds),
        secondsExpression: normalizeString(task.secondsExpression),
        timestamp: normalizeString(task.timestamp),
        timestampExpression: normalizeString(task.timestampExpression)
    };
};

const getResource = (mxResources, key) => mxResources.get(key);

const createDivider = mxResources => {
    const divider = document.createElement('div');
    divider.className = 'ui horizontal divider';
    divider.textContent = getResource(mxResources, 'or');
    return divider;
};

const wrapTimestampInput = timestampField => {
    const timestampInput = timestampField.querySelector('input');
    const calendarWrapper = document.createElement('div');
    calendarWrapper.className = 'ui input left icon';
    const calendarIcon = document.createElement('i');
    calendarIcon.className = 'calendar icon';
    calendarWrapper.appendChild(calendarIcon);
    timestampInput.parentNode.insertBefore(calendarWrapper, timestampInput);
    calendarWrapper.appendChild(timestampInput);
};

const createWaitTaskForm = ({
                                task = {},
                                taskErrors = {},
                                editable = true,
                                mxResources,
                                jsonExpressionHelp = null,
                                onWaitChange = () => {},
                                onTimestampCalendarMount = () => {}
                            } = {}) => {
    let waitTask = normalizeWaitTask(task);

    const container = document.createElement('div');
    container.id = 'waitEditor';
    container.className = 'ui form';

    const secondsField = createInputField({
        label: getResource(mxResources, 'waitSeconds'),
        id: 'seconds',
        name: 'seconds',
        type: 'number',
        min: waitMinimumSeconds,
        className: 'required',
        value: waitTask.seconds,
        error: taskErrors?.fields?.seconds
    });
    const secondsInput = secondsField.querySelector('input');
    secondsInput.addEventListener('change', event => emitWait({seconds: event.target.value}));

    const secondsExpressionField = createInputField({
        label: getResource(mxResources, 'waitSecondsExpression'),
        id: 'secondsExpression',
        name: 'secondsExpression',
        type: 'text',
        className: 'required',
        placeHolder: getResource(mxResources, 'expressionPlaceholder'),
        value: waitTask.secondsExpression,
        error: taskErrors?.fields?.secondsExpression
    });
    const secondsExpressionInput = secondsExpressionField.querySelector('input');
    secondsExpressionInput.addEventListener('change', event => emitWait({secondsExpression: event.target.value}));

    const timestampField = createInputField({
        label: getResource(mxResources, 'waitTimestamp'),
        id: 'timestamp',
        name: 'timestamp',
        type: 'text',
        className: 'required',
        value: waitTask.timestamp,
        error: taskErrors?.fields?.timestamp
    });
    wrapTimestampInput(timestampField);
    const timestampInput = timestampField.querySelector('input');
    timestampField.addEventListener('change', event => emitWait({timestamp: event.target.value}));

    const timestampExpressionField = createInputField({
        label: getResource(mxResources, 'waitTimestampExpression'),
        id: 'waitTimestampExpression',
        name: 'waitTimestampExpression',
        type: 'text',
        className: 'required',
        placeHolder: getResource(mxResources, 'expressionPlaceholder'),
        value: waitTask.timestampExpression,
        error: taskErrors?.fields?.timestampExpression
    });
    const timestampExpressionInput = timestampExpressionField.querySelector('input');
    timestampExpressionInput.addEventListener('change', event => emitWait({timestampExpression: event.target.value}));

    container.appendChild(secondsField);
    container.appendChild(createDivider(mxResources));
    container.appendChild(secondsExpressionField);
    container.appendChild(createDivider(mxResources));
    container.appendChild(timestampField);
    container.appendChild(createDivider(mxResources));
    container.appendChild(timestampExpressionField);

    onTimestampCalendarMount({
        element: timestampField,
        minDate: new Date(),
        minTimeGap: waitMinimumSeconds,
        onChange: timestamp => emitWait({timestamp})
    });

    const syncStructuredFields = () => {
        secondsInput.value = waitTask.seconds;
        secondsExpressionInput.value = waitTask.secondsExpression;
        timestampInput.value = waitTask.timestamp;
        timestampExpressionInput.value = waitTask.timestampExpression;
    };

    const appendJsonExpressionHelp = help => {
        if (!help) {
            return;
        }

        [secondsExpressionField, timestampExpressionField].forEach(field => {
            if (!field.querySelector('details')) {
                field.insertAdjacentHTML('beforeend', help);
            }
        });
    };

    function emitWait(patch) {
        const nextWaitTask = normalizeWaitTask({
            ...waitTask,
            ...patch
        });

        if (isEqual(waitTask, nextWaitTask)) {
            return;
        }

        waitTask = nextWaitTask;
        Object.assign(task, patch);
        onWaitChange(cloneValue(patch), cloneValue(waitTask));
    }

    if (!editable) {
        disableFormControls(container);
    }

    appendJsonExpressionHelp(jsonExpressionHelp);

    return {
        element: container,
        getTask: () => cloneValue(waitTask),
        setTask: value => {
            waitTask = normalizeWaitTask(value);
            Object.assign(task, cloneValue(waitTask));
            syncStructuredFields();
        },
        appendJsonExpressionHelp
    };
};

export {
    createWaitTaskForm,
    normalizeWaitTask,
    waitMinimumSeconds
};
